import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Check } from "lucide-react";

interface VerifyResult {
  sellerName: string;
  treat: string;
  installUrl: string;
}

export default function EventVerify() {
  const params = useParams();
  const token = params.token || "";
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [info, setInfo] = useState<VerifyResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/event/verify/${encodeURIComponent(token)}`, {
          method: "POST",
        });
        if (!res.ok) throw new Error("verify failed");
        const data = (await res.json()) as VerifyResult;
        if (!cancelled) {
          setInfo(data);
          setState("ok");
        }
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        {state === "loading" && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Confirming…
          </div>
        )}

        {state === "error" && (
          <Card className="rounded-2xl">
            <CardContent className="space-y-2 p-6 text-center">
              <h1 className="text-xl font-semibold">This link isn't valid</h1>
              <p className="text-sm text-muted-foreground">
                It may have expired or already been used. Head back to the table and we'll sort you out.
              </p>
            </CardContent>
          </Card>
        )}

        {state === "ok" && info && (
          <Card className="rounded-2xl">
            <CardContent className="space-y-3 p-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <div className="text-4xl">🍪</div>
              <h1 className="text-2xl font-semibold">You're verified!</h1>
              <p className="text-sm text-muted-foreground">
                Show this screen at the {info.sellerName} table to grab your{" "}
                <span className="font-medium text-foreground">{info.treat}</span>.
              </p>
              <div className="rounded-xl border border-primary/15 bg-card-gradient p-4 text-left text-sm text-muted-foreground">
                🚚 Want <span className="font-medium text-foreground">free delivery</span> from {info.sellerName}?
                Download the Stoop app and sign up — your first delivery is on us.
                <div className="mt-2">
                  <a href={info.installUrl} className="font-medium text-primary underline">
                    Download Stoop &amp; sign up
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
